// src/components/Dashboard/RoleDashboard/roleApi.js

/**
 * Decide base API path depending on role (SINGULAR).
 * These are INTERNAL API paths and always use INTERNAL NUMERIC IDS.
 */
export function getEntityBase(role = "agent") {
    if (role === "scout") return "/api/scout";
    if (role === "club") return "/api/club";
    return "/api/agent"; // default
}

/**
 * Slug for CLIENT ROUTES (PUBLIC).
 * Used for browser URLs like:
 *   /agent-profile/:username
 *   /scout-profile/:username
 *   /club-profile/:username
 */
export function getEntitySlug(role = "agent") {
    if (role === "scout") return "scout";
    if (role === "club") return "club";
    return "agent";
}

/**
 * Resolve the best INTERNAL ENTITY ID for the current role.
 * This is ONLY for backend/API calls.
 *
 * Priority:
 *   explicit id →
 *   role-specific id →
 *   other ids →
 *   userId →
 *   null
 */
export function resolveId({
    role = "agent",
    id,
    agentId,
    scoutId,
    clubId,
    userId,
}) {
    if (id) return id;

    if (role === "scout" && (scoutId ?? userId)) return scoutId ?? userId;
    if (role === "club" && (clubId ?? userId)) return clubId ?? userId;

    // default / agent
    return agentId ?? scoutId ?? clubId ?? userId ?? null;
}

/**
 * Build an INTERNAL API path (default pattern):
 *   /api/:role/:id/:path?
 *
 * Example:
 *   rolePath({ role: "agent", agentId: 1 }, "profile")
 *   → /api/agent/1/profile
 *
 * IMPORTANT:
 * Reports have TWO different endpoint shapes in your backend:
 *   POST /api/:role/report         (no :id)
 *   GET  /api/:role/:id/report     (has :id)
 *
 * So: rolePath(..., "report") should return the GET shape:
 *   /api/:role/:id/report
 *
 * And for POST you should use reportSubmitUrl(role) from below.
 */
export function rolePath(ids, path = "") {
    const base = getEntityBase(ids?.role); // "/api/agent" | "/api/scout" | "/api/club"
    const cleanPath = String(path || "").replace(/^\/+/, "");
    const eid = resolveId(ids ?? {});

    if (!eid) {
        if (typeof window !== "undefined") {
            console.warn("[roleApi] Missing entity id for", ids);
        }
        return base;
    }

    return cleanPath ? `${base}/${eid}/${cleanPath}` : `${base}/${eid}`;
}

/**
 * ============================
 * REPORT URL HELPERS (IMPORTANT)
 * ============================
 *
 * Use these for the report endpoints to avoid confusion:
 *
 *   POST /api/:role/report
 *   GET  /api/:role/:id/report
 */
export function reportSubmitUrl(role = "agent") {
    return `${getEntityBase(role)}/report`; // e.g. /api/agent/report
}

export function reportListUrl(role = "agent", actorId) {
    if (!actorId) return `${getEntityBase(role)}/report`;
    return `${getEntityBase(role)}/${actorId}/report`; // e.g. /api/agent/123/report
}

/**
 * ============================
 * Reusable INTERNAL API helpers
 * ============================
 */

export const profileFetchUrl = (role, eid) =>
    `${getEntityBase(role)}/${eid}/profile`;

export const profileUploadUrl = (role, eid) =>
    `${getEntityBase(role)}/${eid}/profile/upload`;

export const inboxUrl = (role, eid) => `${getEntityBase(role)}/${eid}/inbox`;

export const opportunitiesUrl = (role, eid) =>
    `${getEntityBase(role)}/${eid}/opportunities`;

/**
 * ============================
 * PUBLIC ROUTE helper (USERNAME-based)
 * ============================
 * Browser navigation only — NOT API.
 *
 * Example:
 *   getPublicProfilePath("agent", "john_doe")
 *   → /agent-profile/john_doe
 */
export function getPublicProfilePath(role, username) {
    if (!username) return null;
    const slug = getEntitySlug(role);
    return `/${slug}-profile/${username}`;
}



  
  